import React, { useEffect, useRef, useState } from 'react';
import {
  NJButton,
  NJIconButton,
  NJInlineMessage,
  NJSidebarBrandDeprecated,
  NJSidebarContentDeprecated,
  NJSidebarDeprecated,
  NJSidebarDividerDeprecated,
  NJSidebarMenuDeprecated,
  NJSpinner,
  NJToastService
} from '@engie-group/fluid-design-system-react';

import { messageSource, NJChatBlock } from './components/NJChatBlock';
import { NJChatTextMessage } from './components/NJChatTextMessage';
import { NJChatExchange } from './components/NJChatExchange';

import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChatSidebarItem, SidebarFooter } from '@genai/sidebar';
import { Assistant, AssistantId, loadAssistants } from '@genai/assistant';
import { useMediaQuery } from '@genai/browser';

import { useChat } from './useChat.hook';
import { MarkdownMessage } from './message';
import styles from './page.module.css';
import { loadChats } from '../loaders';
import { type ChatId, role, status } from '../model';
import { ChatError } from './chatError';
import { AssistantMessage } from './assistantMessage';
import { initialMessageFromAssistant } from './initialMessageFromAssistant';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChatInput } from './chatInput';
import { removeChat } from './deleteChat.ts';
import { StartConversation } from './startConversation.tsx';
import { NJSelectedEquipmentBanner } from './components/NJSelectedEquipmentBanner.tsx';

export const ChatPage = (): React.ReactElement => {
  const isMobile = useMediaQuery('(max-width: 992px)');
  const [isSidebarFolded, setIsSidebarFolded] = useState(true);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState<{ [key: string]: boolean }>({});
  const queryClient = useQueryClient();

  const { assistantId, chatId } = useParams() as {
    assistantId: AssistantId;
    chatId: ChatId | undefined;
  };


  const { data: assistants, isLoading: assistantsLoading } = useQuery({
    queryKey: ['assistants'],
    queryFn: loadAssistants
  });

  const { data: chats, isLoading: chatsLoading } = useQuery({
    queryKey: ['chats', assistantId],
    queryFn: () => loadChats(assistantId as AssistantId)
  });

  const currentChat = chatId && chats ? chats.find(chat => chat.uuid === chatId) : undefined;

  const { chatMessages, files, sendMessage, isChatLoading, createNewChat } = useChat();

  const isLoading = assistantsLoading || chatsLoading;

  const chatContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
    chatContainerRef.current?.scrollTo;
  }, [chatMessages]);

  const currentAssistant = assistants?.find(
    (assistant) => assistant.uuid === assistantId
  );

  const toggleMenu = (chatUuid: string) => {
    setMenuOpen((prev) => ({
      ...prev,
      [chatUuid]: !prev[chatUuid]
    }));
  };

  const handleDelete = async (assistantId: AssistantId, chatId: ChatId) => {
    try {
      await removeChat(assistantId, chatId);

      setMenuOpen((prev) => ({
        ...prev,
        [chatId]: false
      }));

      await queryClient.invalidateQueries({
        queryKey: ['chats', assistantId as string]
      });

      navigate(`/assistant/${assistantId}/chat`);

      NJToastService('Conversation supprimée avec succès !');
    } catch (error) {
      NJToastService(`Échec de la suppression de la conversation. ${error}`);
    }
  };

  const startConversation = async (assistant: Assistant, selectedBrand: string, selectedModel: string) => {
    const [navigate] = await createNewChat(assistant.uuid, selectedBrand, selectedModel);
    await queryClient.invalidateQueries({
      queryKey: ['chats', assistantId as string]
    });
    navigate();
  };

  return (
    <>
      <NJSidebarDeprecated isFolded={isMobile ? isSidebarFolded : false}>
        <NJSidebarBrandDeprecated imageSrc="/techhelp-logo.png" />
        <NJSidebarMenuDeprecated className={'genai-sidebar-menu'}>
          <div>
            <div className="genai-block_btn_new">
              <Link
                className="genai-sidebar-new"
                to={`/assistant/${assistantId}/chat`}
              >
                <NJButton
                  className="genai-sidebar-btn-new"
                  icon="add_circle_outline"
                  label="Nouvelle discussion"
                  variant="secondary"
                />
              </Link>
            </div>
          </div>

          <NJSidebarDividerDeprecated className="genai-sidebar-divider" />

          <div className={styles['genai-sidebar-menu-content']}>
            <h4 className={styles['genai-sidebar-content-title']}>
              Liste des discussions
            </h4>

            {chats?.map((chat) => (
              <ChatSidebarItem
                key={chat.uuid}
                chat={chat}
                chatId={chatId}
                assistantId={assistantId}
                toggleMenu={toggleMenu}
                menuOpen={menuOpen}
                handleDelete={handleDelete}
                setMenuOpen={setMenuOpen}
              ></ChatSidebarItem>
            ))}
          </div>
        </NJSidebarMenuDeprecated>

        <SidebarFooter closeSidebar={() => setIsSidebarFolded(true)} />
      </NJSidebarDeprecated>

      <NJSidebarContentDeprecated className={styles['genai-sidebar-content']}>
        <div className={styles['genai-sidebar-content-container']}>
          <header>
            <NJIconButton
              icon="menu"
              variant="tertiary"
              size="md"
              label="sidebar toggle"
              onClick={() => setIsSidebarFolded(!isSidebarFolded)}
            />

            <NJSidebarBrandDeprecated
              imageSrc="/techhelp-logo.png"
              className={styles['genai-sidebar-content-mobile-logo']}
            />

            <Link
              className={styles['genai-sidebar-new-chat-button']}
              to={`/assistant/${assistantId}/chat`}
            >
              <NJIconButton
                icon="chat"
                variant="tertiary"
                size="md"
                label="Nouvelle discussion"
              />
            </Link>
          </header>

          <div className={styles['genai-sidebar-content-container__content']}>
            {
              !chatId ?
                (

                  <div className={styles['genai-sidebar-content-start-conversation__wrapper']}>
                    <div className={styles['genai-sidebar-content-start-conversation']}>
                      {currentAssistant && (
                        <StartConversation
                          assistant={currentAssistant}
                          startConversation={startConversation}
                        />
                      )}
                    </div>
                  </div>
                )
                :
                (
                  <>
                    {currentChat &&
                      (
                        <NJSelectedEquipmentBanner
                          assistantId={assistantId}
                          brand={currentChat.brand}
                          model={currentChat.model}
                        />
                      )
                    }

                    <div className={styles['genai-sidebar-content-wrapper']}>
                      <div
                        ref={chatContainerRef}
                        className={styles['genai-sidebar-content-chat-wrapper']}
                      >
                        {isLoading ? (
                          <div className={styles['genai-sidebar-content-loader']}>
                            <NJSpinner scale="sm" aria-label=""></NJSpinner>
                          </div>
                        ) : (
                          <>
                            {chatMessages.length > 0 || currentAssistant?.disclaimer ? (
                              <NJChatExchange>
                                <>
                                  {currentAssistant?.disclaimer ? (
                                    <NJChatBlock
                                      className={
                                        styles['genai-chat-ai-chat-exchange-received']
                                      }
                                      source={messageSource.received}
                                    >
                                      <MarkdownMessage
                                        content={initialMessageFromAssistant(
                                          currentAssistant
                                        )}
                                      />
                                      <NJInlineMessage variant="discovery">
                                        <p data-child-name="njInlineMessageTitle">
                                          <MarkdownMessage
                                            content={currentAssistant?.disclaimer}
                                          />
                                        </p>
                                      </NJInlineMessage>
                                      {/* TODO: Request Style appending to InlineMessage */}
                                      <div style={{ marginBottom: '16px' }}></div>
                                    </NJChatBlock>
                                  ) : null}

                                  {isChatLoading ? (
                                    <NJSpinner scale="sm" aria-label="" />
                                  ) : (
                                    <>
                                      {
                                        (files.length > 0 ||
                                          chatMessages.filter(
                                            (message) =>
                                              message?.filters?.hidden === 'true'
                                          ).length > 0) && (
                                          <NJChatBlock
                                            className={
                                              styles[
                                                'genai-chat-ai-chat-exchange-received'
                                                ]
                                            }
                                            source={messageSource.received}
                                          >
                                            <p>
                                              Vous avez téléchargé les fichiers suivants, l'IA les a en mémoire :
                                            </p>
                                            <NJInlineMessage variant="information">
                                              {files.length > 0 && (
                                                <ul
                                                  className={
                                                    styles[
                                                      'genai-chat-ai-chat-inlineMessage-ul'
                                                      ]
                                                  }
                                                >
                                                  {files.map((file) => (
                                                    <li
                                                      className={'file'}
                                                      key={file.uuid}
                                                    >
                                                      <b>{file.name}</b>
                                                    </li>
                                                  ))}
                                                </ul>
                                              )}
                                              {chatMessages && (
                                                <ul
                                                  className={
                                                    styles[
                                                      'genai-chat-ai-chat-inlineMessage-ul'
                                                      ]
                                                  }
                                                >
                                                  {chatMessages
                                                    .filter(
                                                      (message) =>
                                                        message?.filters?.hidden ===
                                                        'true'
                                                    )
                                                    .map((message) => (
                                                      <li
                                                        className={'message'}
                                                        key={message.uuid}
                                                      >
                                                        <b>
                                                          {
                                                            message.content.split(
                                                              '---'
                                                            )[0]
                                                          }
                                                        </b>
                                                      </li>
                                                    ))}
                                                </ul>
                                              )}
                                            </NJInlineMessage>
                                            {/* TODO: Request Style appending to InlineMessage */}
                                            <div style={{ marginBottom: '16px' }}></div>
                                          </NJChatBlock>
                                        )
                                      }
                                      {chatMessages
                                        .filter(
                                          (message) =>
                                            !(message?.filters?.hidden === 'true')
                                        )
                                        .map((message) =>
                                          message.role === role.assistant ? (
                                            <AssistantMessage
                                              assistantId={assistantId}
                                              chatId={chatId}
                                              key={message.uuid}
                                              message={message}
                                            ></AssistantMessage>
                                          ) : (
                                            <React.Fragment key={message.uuid}>
                                              {message.status === status.error ? (
                                                <ChatError message={message.error} />
                                              ) : (
                                                <NJChatBlock
                                                  key={message.uuid}
                                                  source={messageSource.sent}
                                                >
                                                  <NJChatTextMessage
                                                    className={
                                                      styles['genai-chat-ai-chat-exchange-sent']
                                                    }
                                                  >
                                                    {message.content}
                                                  </NJChatTextMessage>
                                                </NJChatBlock>
                                              )}
                                            </React.Fragment>
                                          )
                                        )}
                                    </>
                                  )}
                                </>
                              </NJChatExchange>
                            ) : (
                              <p className={styles['genai-chat-ai-chat-placeholder']}>
                                {initialMessageFromAssistant(currentAssistant)}
                              </p>
                            )}
                          </>
                        )}
                      </div>

                      <div
                        className={styles['genai-sidebar-content-chat-input-container']}
                      >
                        <ChatInput
                          disabled={
                            chatMessages.filter(
                              (message) =>
                                message.status === status.streaming ||
                                message.status === status.pending
                            ).length > 0 || isLoading
                          }
                          sendMessage={async (value) => {
                            await sendMessage(value, undefined, chatId);
                          }}
                        />
                      </div>
                    </div>
                  </>
                )
            }
          </div>


        </div>
      </NJSidebarContentDeprecated>
    </>
  )
    ;
};
